import { Demographic } from '@/common/models';
import { DASH } from '@/globals';

export default class KantarUtils {
  public static codes = {
    demographic: 'std:',
    age: 'a:',
    gender: 'g:',
  };

  public static getKantarDemographics(demographics: Demographic[], dataProviderId: number): Demographic[] {
    return demographics.filter((d) => d.dataProviderId === dataProviderId);
  }

  public static isAgeOrGenderDemographic(demographic: Demographic): boolean {
    return demographic?.code?.includes(this.codes.age) || demographic?.code?.includes(this.codes.gender);
  }

  public static countCodes(demographic: Demographic): number {
    return [this.codes.age, this.codes.gender].filter((code) => demographic?.code?.includes(code))?.length;
  }

  public static isCombination(demographic: Demographic): boolean {
    return this.countCodes(demographic) > 1;
  }

  public static isAgeRangeValid(demographic: Demographic): boolean {
    return demographic?.code.includes('[') && demographic.code.includes(']');
  }

  public static getAgeRange(demographic: Demographic): { start: string; end: string } | null {
    if (!this.isAgeRangeValid(demographic)) return null;

    const range = demographic.code
      .split(`${this.codes.demographic} ${this.codes.age}`)[1]
      .replace('[', '')
      .replace(']', '')
      .split(',');

    const [start, end] = range;

    return {
      start: start === '' ? DASH : start,
      end: end === '' ? '+' : (Number(end) + 1).toString(),
    };
  }

  public static getCodesFromCombinationCode(demographic: Demographic): string[] {
    return demographic.code.split(`${this.codes.demographic} `)[1].split(' ');
  }

  public static hasTheSamePrimaries(demographic: Demographic, primaryCodes: string[]): boolean {
    const codeCount = this.countCodes(demographic);

    if (codeCount !== primaryCodes.length) return false;

    const matchingCodes = primaryCodes.filter((c) => demographic.code.includes(c));
    return matchingCodes?.length === primaryCodes.length;
  }

  public static findCombination(combinations: Demographic[], selectedDemographics: Demographic[]): Demographic {
    // we want the codes without the prefix
    const selectedPrimaryCodes = selectedDemographics.map((d) => d.code.split(`${this.codes.demographic} `)[1]);
    return combinations.find((d) => this.hasTheSamePrimaries(d, selectedPrimaryCodes));
  }

  public static getCombinations(demographics: Demographic[]): Demographic[] {
    return demographics?.filter((d) => this.isCombination(d));
  }

  public static isPrimaryDemographic(demographic: Demographic): boolean {
    return this.countCodes(demographic) === 1;
  }
}
